import { format } from 'sql-formatter';

interface Token {
  type: 'comment' | 'string' | 'word' | 'symbol';
  value: string;
}

const tokenizeSql = (input: string): Token[] => {
  const tokens: Token[] = [];
  if (!input) return tokens;

  // Updated regex to handle compound operators as single tokens and curly braces
  const regex =
    /(--.*$)|('([^']|'')*')|("([^"]|"")*")|<=|>=|<>|!=|==|[A-Za-z_][A-Za-z0-9_]*|\d+(\.\d+)?|[(){},*/+\-<>=]|;|\./gm;
  let match;

  while ((match = regex.exec(input)) !== null) {
    const value = match[0];
    if (match[1]) {
      tokens.push({ type: 'comment', value: value });
    } else if (value[0] === "'" || value[0] === '"') {
      tokens.push({ type: 'string', value: value });
    } else if (/^[A-Za-z_]/.test(value)) {
      tokens.push({ type: 'word', value: value });
    } else {
      tokens.push({ type: 'symbol', value: value });
    }
  }
  return tokens;
};

const matchKeywordSeq = (tokens: Token[], index: number, seq: string[]): boolean => {
  if (index + seq.length > tokens.length) return false;
  for (let i = 0; i < seq.length; i++) {
    const t = tokens[index + i];
    if (t.type !== 'word') return false;
    if (t.value.toUpperCase() !== seq[i]) return false;
  }
  return true;
};

const isTopLevelClause = (kw: string): boolean => {
  const clauses = [
    'SELECT',
    'FROM',
    'WHERE',
    'GROUP',
    'HAVING',
    'ORDER',
    'INSERT',
    'UPDATE',
    'DELETE',
    'WITH',
    'UNION',
    'EXCEPT',
    'INTERSECT',
    'OVER',
    'WINDOW',
    'JOIN',
    'LEFT',
    'RIGHT',
    'FULL',
    'INNER',
    'CROSS',
  ];
  return clauses.indexOf(kw) !== -1;
};

const repeatSpace = (count: number): string => {
  return new Array(count + 1).join(' ');
};

/**
 * Advanced SQL formatter with support for MATCH_RECOGNIZE and complex CASE expressions
 * @param sql - The SQL string to format
 * @returns Formatted SQL string with proper indentation and line breaks
 */
export const formatComplexSQL = (sql: string): string => {
  const tokens = tokenizeSql(sql);
  if (!tokens.length) return sql || '';

  const lines: string[] = [];
  let currentLine = '';
  let indent = 0;

  let inMatch = false;
  let inMeasures = false;
  let inDefine = false;
  let matchParenDepth = 0;

  // CASE / WHEN / THEN / ELSE / END indentation
  const caseStack: Array<{ indent: number }> = [];

  const pushLine = () => {
    const trimmed = currentLine.replace(/\s+$/g, '');
    lines.push(trimmed);
    currentLine = '';
  };

  const ensureLine = () => {
    if (!currentLine) currentLine = repeatSpace(indent);
  };

  const newLine = (newIndent?: number) => {
    if (currentLine && currentLine.trim() !== '') {
      pushLine();
    }
    if (typeof newIndent === 'number') {
      indent = newIndent;
    }
    currentLine = repeatSpace(indent);
  };

  const appendToken = (str: string, needSpaceBefore?: boolean) => {
    if (!str) return;
    if (!currentLine) {
      currentLine = repeatSpace(indent);
      currentLine += str;
      return;
    }
    if (needSpaceBefore === false) {
      currentLine += str;
    } else {
      const ch = currentLine[currentLine.length - 1];
      // Modified: treat '.' like whitespace/parenthesis so next token after '.' is not prefixed with a space (keeps a.b intact)
      if (!ch || /[\s.(]/.test(ch)) {
        currentLine += str;
      } else {
        currentLine += ' ' + str;
      }
    }
  };

  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];

    if (t.type === 'comment') {
      // Handle comments with proper indentation
      if (currentLine && currentLine.trim() !== '') {
        pushLine();
      }
      // Preserve indentation for comments based on current context
      const commentLine = repeatSpace(indent) + t.value;
      lines.push(commentLine);
      currentLine = '';
      i++;
      continue;
    }

    if (t.type === 'string') {
      ensureLine();
      appendToken(t.value, true);
      i++;
      continue;
    }

    if (t.type === 'word') {
      const upper = t.value.toUpperCase();

      // Multi-word MATCH_RECOGNIZE clauses
      if (matchKeywordSeq(tokens, i, ['ONE', 'ROW', 'PER', 'MATCH'])) {
        if (inMatch) {
          newLine(2);
        } else {
          newLine(0);
        }
        appendToken('ONE ROW PER MATCH', true);
        i += 4;
        continue;
      }

      if (matchKeywordSeq(tokens, i, ['AFTER', 'MATCH', 'SKIP'])) {
        if (inMatch) {
          newLine(2);
        } else {
          newLine(0);
        }
        appendToken('AFTER MATCH SKIP', true);
        i += 3;
        continue;
      }

      // CASE expression indentation
      if (upper === 'CASE') {
        ensureLine();
        appendToken('CASE', true);
        caseStack.push({ indent: indent });
        indent = indent + 2;
        i++;
        continue;
      }

      if (upper === 'WHEN' && caseStack.length) {
        const base = caseStack[caseStack.length - 1].indent;
        newLine(base + 2);
        appendToken('WHEN', true);
        i++;
        continue;
      }

      if (upper === 'ELSE' && caseStack.length) {
        const baseElse = caseStack[caseStack.length - 1].indent;
        newLine(baseElse + 2);
        appendToken('ELSE', true);
        i++;
        continue;
      }

      if (upper === 'THEN' && caseStack.length) {
        appendToken('THEN', true);
        i++;
        continue;
      }

      if (upper === 'END' && caseStack.length) {
        const ctx = caseStack.pop()!;
        newLine(ctx.indent);
        appendToken('END', true);
        indent = ctx.indent;
        i++;
        continue;
      }

      if (upper === 'MATCH_RECOGNIZE') {
        // Start MATCH_RECOGNIZE block at top-level
        newLine(0);
        appendToken('MATCH_RECOGNIZE', true);
        inMatch = true;
        inMeasures = false;
        inDefine = false;
        i++;
        continue;
      }

      if (upper === 'MEASURES' && inMatch) {
        // Line for MEASURES, then subsequent expressions at 4 spaces
        newLine(2);
        appendToken('MEASURES', true);
        pushLine();
        indent = 4;
        inMeasures = true;
        inDefine = false;
        currentLine = repeatSpace(indent);
        i++;
        continue;
      }

      if (upper === 'DEFINE' && inMatch) {
        newLine(2);
        appendToken('DEFINE', true);
        inDefine = true;
        inMeasures = false;
        i++;
        continue;
      }

      // Handle logical operators in DEFINE section
      if (inDefine && (upper === 'AND' || upper === 'OR')) {
        newLine(6); // Fixed indentation for logical operators in DEFINE
        appendToken(upper, true);
        i++;
        continue;
      }

      // Handle variable names in DEFINE section (like X, OTHERS)
      if (inDefine && upper.length <= 10 && /^[A-Z][A-Z_]*$/.test(upper)) {
        // Check if next token is AS
        if (
          i + 1 < tokens.length &&
          tokens[i + 1].type === 'word' &&
          tokens[i + 1].value.toUpperCase() === 'AS'
        ) {
          newLine(4);
          appendToken(upper, true);
          i++;
          continue;
        }
      }

      if (upper === 'PATTERN' && inMatch) {
        newLine(2);
        appendToken('PATTERN', true);
        i++;
        continue;
      }

      // ORDER BY inside MATCH_RECOGNIZE should be indented 2 spaces
      if (upper === 'ORDER') {
        if (inMatch && matchKeywordSeq(tokens, i, ['ORDER', 'BY'])) {
          newLine(2);
          appendToken('ORDER BY', true);
          i += 2;
          continue;
        }
      }

      // Normal top-level clauses at column 0
      if (!inMatch && isTopLevelClause(upper)) {
        newLine(0);
        appendToken(upper, true);
        i++;
        continue;
      }

      // Default word handling
      ensureLine();
      appendToken(t.value, true);
      i++;
      continue;
    }

    if (t.type === 'symbol') {
      const sym = t.value;

      if (sym === '(') {
        // For MATCH_RECOGNIZE ( keep "(" on same line
        if (!currentLine) ensureLine();
        appendToken('(', false);
        if (inMatch) {
          matchParenDepth++;
          // Only add moderate indentation for the first few levels in DEFINE
          if (inDefine && matchParenDepth <= 3) {
            // Don't add line breaks for every parenthesis - keep it more compact
          }
        }
        i++;
        continue;
      }

      if (sym === ')') {
        if (inMatch && matchParenDepth === 1) {
          // Closing the MATCH_RECOGNIZE block – put ")" at column 0
          if (currentLine && currentLine.trim() !== '') {
            pushLine();
          }
          indent = 0;
          currentLine = repeatSpace(indent);
          appendToken(')', false);
          pushLine();
          currentLine = '';
          inMatch = false;
          inMeasures = false;
          inDefine = false;
          matchParenDepth = 0;
          i++;
          continue;
        } else {
          if (inMatch && matchParenDepth > 0) {
            matchParenDepth--;
            // Don't modify indentation for closing parentheses in DEFINE
            ensureLine();
            appendToken(')', false);
          } else {
            ensureLine();
            appendToken(')', false);
          }
          i++;
          continue;
        }
      }

      if (sym === ',') {
        ensureLine();
        appendToken(',', false);
        if (inMeasures) {
          // One MEASURES expression per line
          pushLine();
          indent = 4;
          currentLine = repeatSpace(indent);
        } else if (inDefine) {
          // One DEFINE variable per line with consistent indentation
          pushLine();
          indent = 4;
          currentLine = repeatSpace(indent);
        } else {
          currentLine += ' ';
        }
        i++;
        continue;
      }

      if (sym === ';') {
        ensureLine();
        appendToken(';', false);
        pushLine();
        indent = 0;
        currentLine = '';
        i++;
        continue;
      }

      if (sym === '{' || sym === '}') {
        // Keep curly braces together with adjacent text for PATTERN syntax
        ensureLine();
        appendToken(sym, false);
        i++;
        continue;
      }

      // Other operators/symbols
      ensureLine();
      if (/^(<=|>=|<>|!=|==|[<>*/+-])$/.test(sym)) {
        currentLine += ' ' + sym + ' ';
      } else if (sym === '=') {
        currentLine += ' = ';
      } else if (sym === '.') {
        appendToken('.', false);
      } else {
        appendToken(sym, false);
      }
      i++;
      continue;
    }

    // Any other token type: just append raw
    ensureLine();
    appendToken(t.value || '', true);
    i++;
  }

  if (currentLine && currentLine.trim() !== '') {
    pushLine();
  }

  return lines.join('\n');
};

/**
 * Flattens SQL by replacing all whitespace with single spaces
 * @param sql - The SQL string to flatten
 * @returns Single-line SQL string
 */
export const flattenSQL = (sql: string): string => {
  return sql;
  // return sql.replace(/\s+/g, ' ').trim();
};

/**
 * Configures SQL language support and completion provider for Monaco Editor
 * @param monaco - The Monaco Editor instance
 */
export const configureSqlLanguage = (monaco: any): void => {
  // Configure SQL language with brackets and auto-closing pairs
  monaco.languages.setLanguageConfiguration('sql', {
    brackets: [
      ['(', ')'],
      ['[', ']'],
    ],
    autoClosingPairs: [
      { open: '(', close: ')' },
      { open: '[', close: ']' },
      { open: "'", close: "'" },
      { open: '"', close: '"' },
    ],
    surroundingPairs: [
      { open: '(', close: ')' },
      { open: '[', close: ']' },
      { open: "'", close: "'" },
      { open: '"', close: '"' },
    ],
  });

  // Register SQL completion provider with keywords and document words
  monaco.languages.registerCompletionItemProvider('sql', {
    provideCompletionItems: (model: any) => {
      const sqlKeywords = [
        'SELECT',
        'FROM',
        'WHERE',
        'INSERT',
        'UPDATE',
        'DELETE',
        'JOIN',
        'INNER',
        'LEFT',
        'RIGHT',
        'OUTER',
        'FULL',
        'GROUP BY',
        'ORDER BY',
        'HAVING',
        'LIMIT',
        'OFFSET',
        'DISTINCT',
        'UNION',
        'INTERSECT',
        'EXCEPT',
        'CREATE',
        'ALTER',
        'DROP',
        'TABLE',
        'INDEX',
        'VIEW',
        'AND',
        'OR',
        'NOT',
        'NULL',
        'IS',
        'IN',
        'EXISTS',
        'COUNT',
        'SUM',
        'AVG',
        'MAX',
        'MIN',
        'ROUND',
        'UPPER',
        'LOWER',
        'CASE',
        'WHEN',
        'THEN',
        'ELSE',
        'END',
        'AS',
        'ON',
        'INTO',
        'VALUES',
        'SET',
        'PRIMARY',
        'KEY',
        'FOREIGN',
      ];

      const suggestions: any[] = [];

      // Add SQL keywords
      sqlKeywords.forEach((keyword) => {
        suggestions.push({
          label: keyword,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: keyword,
          documentation: `SQL keyword: ${keyword}`,
          sortText: '0' + keyword, // Sort keywords first
        });
      });

      // Get words from current document
      const text = model.getValue();
      const words = text.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) || [];
      const uniqueWords = [...new Set(words as string[])].filter(
        (word) => word.length > 2 && !sqlKeywords.includes(word.toUpperCase()),
      );

      // Add document words as table/column suggestions
      uniqueWords.forEach((word) => {
        suggestions.push({
          label: word,
          kind: monaco.languages.CompletionItemKind.Text,
          insertText: word,
          documentation: `Word from document: ${word}`,
          sortText: '1' + word, // Sort document words after keywords
        });
      });

      return { suggestions };
    },
  });
};

/**
 * Comprehensive SQL formatter that tries multiple approaches
 * @param sql - The SQL string to format
 * @returns Formatted SQL string
 */
export const formatSQL = (sql: string): string => {
  if (!sql || !sql.trim()) return sql;

  let formattedSQL = '';

  // Check if SQL contains MATCH_RECOGNIZE - if so, use custom formatter first
  if (sql.toUpperCase().includes('MATCH_RECOGNIZE')) {
    try {
      formattedSQL = formatComplexSQL(sql);
      if (formattedSQL && formattedSQL.trim()) {
        return formattedSQL;
      }
    } catch (error) {
      // If custom formatter fails, continue to standard formatters
    }
  }

  try {
    // Try sql-formatter with original SQL - no content modification
    formattedSQL = format(sql, {
      language: 'sql',
      tabWidth: 2,
      useTabs: false,
      keywordCase: 'upper',
      dataTypeCase: 'upper',
      functionCase: 'upper',
    });
  } catch (error) {
    // If standard formatter fails, try different dialects
    const dialects: Array<'postgresql' | 'mysql' | 'sqlite'> = ['postgresql', 'mysql', 'sqlite'];

    for (const dialect of dialects) {
      try {
        formattedSQL = format(sql, {
          language: dialect,
          tabWidth: 2,
          useTabs: false,
          keywordCase: 'upper',
          dataTypeCase: 'upper',
          functionCase: 'upper',
        });
        break;
      } catch (dialectError) {
        continue;
      }
    }

    // If all SQL formatters fail, use custom formatter
    if (!formattedSQL) {
      formattedSQL = formatComplexSQL(sql);
    }
  }

  return formattedSQL || sql;
};
